import { apiAddress } from "../../config/server";
import { IPayRate, IPayrateAttachment } from "../../types/payrates/PayRate";
import { ApiBase } from "./baseApi";

export interface ICreateAssociationPayrateRequest {
  name: string;
  associationId?: string;
  supervisor?: number;
  referee?: {
    rate: Partial<{ [grade: string]: number }>;
    plusRate?: Partial<{ [grade: string]: number }>;
    apprenticeBonus?: number;
    solo: number;
  };
  scoretable?: {
    rate: Partial<{ [grade: string]: number }>;
  };
  refereeCoach?: {
    rate: Partial<{ [grade: string]: number }>;
  };
  statistician?: {
    rate: Partial<{ [grade: string]: number }>;
  };
  admin?: {
    rate: number;
  };
}

export interface IClonePayrateRequest {
  name: string;
  associationId?: string;
}

export interface ICreateAssociationPayrateAttachmentRequest {
  payrateId: string;
  associationId: string;
  start?: Date;
  finish?: Date;
  priority?: number;
  tags?: any[];
}

export default class RatesApi extends ApiBase {
  static RatesApiAddress = `${apiAddress}/api/rates/`;

  // Existing endpoints
  static GetById = RatesApi.RatesApiAddress;
  static Update = (id: string) =>
    `${RatesApi.RatesApiAddress}associations/update/${id}`;

  // New association payrate endpoints
  static AssociationPayrates = `${RatesApi.RatesApiAddress}association`;
  static AssociationPayrateById = (id: string) =>
    `${RatesApi.RatesApiAddress}association/${id}`;
  static ClonePayrate = (id: string) =>
    `${RatesApi.RatesApiAddress}association/${id}/clone`;

  // Association payrate attachment endpoints
  static AttachPayrate = `${RatesApi.RatesApiAddress}associations/attach`;
  static DetachPayrate = (id: string) =>
    `${RatesApi.RatesApiAddress}associations/${id}`;

  // Existing methods
  static getById(id: string): Promise<IPayRate> {
    return this.get<IPayRate>(this.GetById + id).then(
      (response) => response.data.data,
    );
  }

  static getAll(): Promise<IPayRate[]> {
    return this.get<IPayRate[]>(this.GetById).then(
      (response) => response.data.data,
    );
  }

  static updateAssociationPayrate(
    id: string,
    payrate: Partial<IPayrateAttachment>,
  ): Promise<IPayRate> {
    if ("_id" in payrate) {
      delete payrate._id;
    }
    return this.post<IPayRate>(this.Update(id), payrate).then(
      (response) => response.data.data,
    );
  }

  // New association payrate CRUD methods
  static createAssociationPayrate(
    payrate: ICreateAssociationPayrateRequest,
  ): Promise<IPayRate> {
    return this.post<IPayRate>(this.AssociationPayrates, payrate).then(
      (response) => response.data.data,
    );
  }

  static updateAssociationPayrateById(
    id: string,
    updates: Partial<ICreateAssociationPayrateRequest>,
  ): Promise<IPayRate> {
    if ("_id" in updates) {
      delete (updates as any)._id;
    }
    return this.put<IPayRate>(this.AssociationPayrateById(id), updates).then(
      (response) => response.data.data,
    );
  }

  static deleteAssociationPayrateById(id: string): Promise<boolean> {
    return this.delete<boolean>(this.AssociationPayrateById(id)).then(
      (response) => response.data.data,
    );
  }

  static getAssociationPayrates(): Promise<IPayRate[]> {
    return this.get<IPayRate[]>(this.AssociationPayrates).then(
      (response) => response.data.data,
    );
  }

  static clonePayrate(
    sourceId: string,
    cloneData: IClonePayrateRequest,
  ): Promise<IPayRate> {
    return this.post<IPayRate>(this.ClonePayrate(sourceId), cloneData).then(
      (response) => response.data.data,
    );
  }

  // Association payrate attachment methods
  static createAssociationPayrateAttachment(
    attachment: ICreateAssociationPayrateAttachmentRequest,
  ): Promise<IPayrateAttachment> {
    return this.post<IPayrateAttachment>(this.AttachPayrate, attachment).then(
      (response) => response.data.data,
    );
  }

  static deleteAssociationPayrateAttachment(id: string): Promise<boolean> {
    return this.delete<boolean>(this.DetachPayrate(id)).then(
      (response) => response.data.data,
    );
  }
}
